<template>
    <tr class="text-xs-center nowrap">
        <td class="pa-0">
            <div class="cell">
                <v-avatar 
                    v-if="item.locationHasAlert" 
                    :color="item.locationAlertLevelColor"
                    class="mr-1"
                    size="24">
                </v-avatar>
                <span>{{ item.locationName }}</span>
            </div>
        </td>
        <td class="pa-0">
            <div class="cell">
                <v-btn flat block @click="$emit('goToDetail')">
                    <v-avatar 
                        v-if="item.rotationDays" 
                        color="primary"
                        class="mr-1"
                        size="32">
                        <span class="white--text">{{ item.rotationDays }}</span>
                    </v-avatar>
                    <span>{{ item.varietyName }}</span>
                </v-btn>
            </div>
        </td>
        <td class="pa-0">
            <div class="cell">
                <v-avatar 
                    v-if="nextPickDateHasMoved" 
                    class="mr-1"
                    size="32" 
                    color="error">
                    <span class="white--text">{{ nextPickDateMovedDays }}</span>
                </v-avatar>
                <span>{{ nextPickDate }}</span>
            </div>
        </td>
        <td class="pa-0">
            <div class="cell">
                <span v-if="lastPickValue">{{ lastPickValue }}</span>
                <span v-else>N/A</span>
            </div>
        </td>
        <td style="min-width: 156px; padding: 0px;" 
            align="center"
            :id="'forecast-'+item.cropLocationId+'-'+item.varietyId"
            :tabindex="editable && !forecastValueLocked ? 0 : null"
            @focus="openForecastEdit()"
            @keydown.esc="closeForecastEdit(false)"
            @click="openForecastEdit()">
            <template v-if="forecastEditMode">
                <EditTableCell
                    style="width: 156px"
                    label="Forecast"
                    :value="`${nextPickDateForecastValue}`"
                    @input="modifyForecastValue"
                    @close="closeForecastEdit($event)"
                    :rules="[validateForecastValue]"
                    :loading="forecastLoading">
                </EditTableCell>
            </template>
            <template v-else>
                <div :class="isForecastSpray && nextPickDateForecastValue !== 0 ? 'error lighten-4 cell' : 'cell'">
                    <span v-if="`${nextPickDateForecastValue}`">{{ nextPickDateForecastValue }}</span>
                    <span v-else>N/A</span>
                    <v-icon v-if="forecastValueLocked">lock_outline</v-icon>
                </div>
            </template>
        </td>
        <td style="min-width: 156px; padding: 0px;"
            align="center"
            :id="'adjusted-'+item.cropLocationId+'-'+item.varietyId"
            :tabindex="editable && !adjustedValueLocked ? 0 : null"
            @focus="openAdjustedForecastEdit()"
            @keydown.esc="closeAdjustedForecastEdit(false)"
            @click="openAdjustedForecastEdit()">
            <template v-if="adjustedForecastEditMode">
                <EditTableCell
                    style="width: 156px"
                    label="Forecast"
                    :value="`${nextPickDateForecastAdjusted}`"
                    @input="modifyAdjustedValue"
                    @close="closeAdjustedForecastEdit($event)"
                    :rules="[validateForecastValue]"
                    :loading="adjustedForecastLoading">
                </EditTableCell>
            </template>
            <template v-else>
                <div :class="isAdjustedSpray && nextPickDateForecastAdjusted !== 0 ? 'error lighten-4 cell' : 'cell'">
                    <span v-if="`${nextPickDateForecastAdjusted}`">{{ nextPickDateForecastAdjusted }}</span>
                    <span v-else>N/A</span>
                    <v-icon v-if="adjustedValueLocked">lock_outline</v-icon>
                </div>
            </template>
        </td>
    </tr>
</template>

<script>
import EditTableCell from '@/components/forecasting/EditTableCell.vue'

import { mapGetters, mapActions } from "vuex"
import momentCache from "@/services/momentCache";

export default {
    components: {
        EditTableCell
    },
    props: {
        item: {
            type: Object,
            required: true
        }
    },
    data(){
        return {
            forecastEditMode: false,
            forecastLoading: false,
            adjustedForecastEditMode: false,
            adjustedForecastLoading: false
        };
    },
    computed: {
        ...mapGetters("ForecastingStore", [
            "editable"
        ]),
        nextPickDate(){
            return this.$options.filters.formatDate(this.item.nextPickDate);
        },
        nextPickDateHasMoved(){
            return this.item.nextPickDateMovedDays != 0;
        },
        nextPickDateMovedColor(){
            if(this.item.nextPickDateMovedDays < 0){
                return "error";
            }

            if(this.item.nextPickDateMovedDays > 0){
                return "primary";
            }

            return "";
        },
        nextPickDateMovedDays(){
            let amount = Math.abs(this.item.nextPickDateMovedDays);

            if(this.item.nextPickDateMovedDays >= 0){
                return `+${amount}`;
            } else {
                return `-${amount}`;
            }
        },
        lastPickValue(){
            return parseInt(this.item.lastPickActualValue);
        },
        nextPickDateForecastValue(){
            return this.item.nextPickDateForecast.forecastValue;
        },
        nextPickDateForecastAdjusted(){
            if(this.item.nextPickDateForecast.adjustedValue === "") {
                return this.nextPickDateForecastValue;
            }
           
            return this.item.nextPickDateForecast.adjustedValue;
        },
        forecastValueLocked(){
            return this.item.nextPickDateForecast.forecastValueLocked;
        },
        adjustedValueLocked(){
            return this.item.nextPickDateForecast.adjustedValueLocked;
        },
        isForecastSpray(){
            return this.item.nextPickDateForecast.isSpray && !this.forecastValueLocked;
        },
        isAdjustedSpray() {
            return this.item.nextPickDateForecast.isSpray && !this.adjustedValueLocked;
        }
    },
    methods: {
        ...mapActions("ForecastingStore", [
            "handleForecastChanged",
            "updateData"
        ]),
        openForecastEdit(){
            if(!this.editable){
                return;
            }

            if(this.forecastValueLocked){
                return;
            }

            this.forecastEditMode = true;
        },
        closeForecastEdit(isEnter){
            this.forecastEditMode = false;
            if(isEnter && this.item.nextListItem) {
                document.getElementById('forecast-'+this.item.nextListItem.cropLocationId+'-'+this.item.nextListItem.varietyId).click();
            }
        },
        openAdjustedForecastEdit(){
            if(!this.editable){
                return;
            }

            if(this.adjustedValueLocked){
                return;
            }

            this.adjustedForecastEditMode = true;
        },
        closeAdjustedForecastEdit(isEnter){
            this.adjustedForecastEditMode = false;
            if(isEnter && this.item.nextListItem) {
                document.getElementById('adjusted-'+this.item.nextListItem.cropLocationId+'-'+this.item.nextListItem.varietyId).click();
            }
        },
        validateForecastValue(v){
            if (this.isForecastSpray || this.isAdjustedSpray) {
                if(v == null || !v.match(/^[0]$/)) {
                    return "Only zero can be captured on a Spray day";
                }
            }

            if(v == null || !v.match(/^[0-9]{1,5}$/)){
                return "Please enter a number in the form XXXXX";
            }

            return false;
        },
        async modifyForecastValue(event){
            var newValue = event.value;
            var isEnter = event.isEnter;
            this.forecastLoading = true;

            let nextPickDate = momentCache.getMoment(this.item.nextPickDate).format('YYYY-MM-DD')

            await this.handleForecastChanged({
                cropLocationId: this.item.cropLocationId,
                varietyId: this.item.varietyId,
                date: nextPickDate,
                forecastValue: newValue === "" ? null : newValue,
                adjustedValue: (this.nextPickDateForecastAdjusted === "" || this.adjustedValueLocked) ? null : this.nextPickDateForecastAdjusted
            })

            await this.updateData();

            this.forecastLoading = false;
            this.closeForecastEdit(isEnter);
        },
        async modifyAdjustedValue(event){
            var newValue = event.value;
            var isEnter = event.isEnter;
            this.adjustedForecastLoading = true;

            let nextPickDate = momentCache.getMoment(this.item.nextPickDate).format('YYYY-MM-DD')

            await this.handleForecastChanged({
                cropLocationId: this.item.cropLocationId,
                varietyId: this.item.varietyId,
                date: nextPickDate,
                forecastValue: this.nextPickDateForecastValue === "" ? null : this.nextPickDateForecastValue,
                adjustedValue: newValue === "" ? null : newValue,
            })

            await this.updateData();

            this.adjustedForecastLoading = false;
            this.closeAdjustedForecastEdit(isEnter);
        }
    },
    created(){
        
    }
}
</script>

<style scoped>
    .nowrap {
        white-space: nowrap;
    }

    .cell {
        height: 60px;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
    }

    table tbody td {
        font-size: 18px;;
    }
</style>